import {
  FieldValueView,
  type FieldValueViewProps,
} from '@databases/fields/FieldValueView';
import { defaultStoreUtils } from '@databases/redux';
import { isEmptyValue } from '@databases/utils/database/field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent } from 'react';

export interface DatabaseValueViewProps
  extends Omit<FieldValueViewProps, 'field' | 'value'> {
  contentId: string;
  fieldId: string;
  entryId: string;
}

/**
 * Renders a database value. The right view component is automatically chosen based on the field type of the value.
 */
const DatabaseValueView: FunctionComponent<DatabaseValueViewProps> = ({
  contentId,
  fieldId,
  entryId,
  showFieldNamePlaceholder,
  ...props
}) => {
  const [field, value, shouldShowFieldNamePlaceholder] =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseByContentIdRequired(state, {
        contentId,
      });
      const entry = getDatabaseEntryRequired(databaseDraft, entryId);
      const field = getDatabaseFieldRequired(databaseDraft, fieldId);
      const value = getConvertedDatabaseValue(databaseDraft, field, entry, state);

      let shouldShowFieldNamePlaceholder = false;

      if (showFieldNamePlaceholder && entryId) {
        shouldShowFieldNamePlaceholder = isEmptyValue(
          { database: databaseDraft, field, entry, value },
          state
        );
      }

      return [field, value, shouldShowFieldNamePlaceholder] as const;
    });

  return (
    <FieldValueView
      {...props}
      contentId={contentId}
      entryId={entryId}
      field={field}
      value={value}
      showFieldNamePlaceholder={shouldShowFieldNamePlaceholder}
    />
  );
};

export default React.memo(DatabaseValueView);
